import React, { useState } from 'react';
import {
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  makeStyles,
  CircularProgress,
} from '@material-ui/core';
import { EarlyDiscountOptionType, IEarlyDiscountOptionInfoDto } from 'typescript/interfaces/deals';
import { convertToPriceFormat } from 'utils/formatters';
import { format } from 'date-fns';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    padding: theme.spacing(3),
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  labelBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  optionWrapper: {
    marginBottom: theme.spacing(2),
  },
  optionTitle: {
    fontWeight: 500,
    fontSize: '1.075rem',
  },
  detailText: {
    fontSize: '1rem',
    color: theme.palette.text.secondary,
  },
  smallRadio: {
    padding: theme.spacing(0.5),
    marginRight: theme.spacing(2),
    transform: 'scale(0.85)',
  },
}));

interface Props {
  initValues: {
    selectedType: EarlyDiscountOptionType | undefined;
  };
  options: IEarlyDiscountOptionInfoDto[];
  submit: (values: { selectedType: EarlyDiscountOptionType | null}) => void;
  cancel: () => void;
  isLoading?: boolean;
}

export const SelectEarlyDiscountForm: React.FC<Props> = ({
  initValues,
  options,
  submit,
  cancel,
  isLoading,
}) => {
  const classes = useStyles();
  const [selectedType, setSelectedType] = useState<EarlyDiscountOptionType | null | undefined>(
    initValues.selectedType ?? null
  );

  const handleSubmit = () => {
    submit({ selectedType: selectedType ?? null });
  };

  return (
    <div className={classes.root}>
      <Typography variant="h3" color="textSecondary">
        Select Early Discount Option
      </Typography>

      <RadioGroup
        value={selectedType ?? '__none__'}
        onChange={(e) => {
          const val = e.target.value;
          setSelectedType(val === '__none__' ? null : (val as EarlyDiscountOptionType));
        }}
      >
        {options.map((option) => (
          <div key={option.type} className={classes.optionWrapper}>
            <FormControlLabel
              value={option.type}
              control={<Radio color="secondary" className={classes.smallRadio} />}
              label={
                <div className={classes.labelBox}>
                  <Typography className={classes.optionTitle} color="textSecondary">
                    {option.type.replace(/([A-Z])/g, ' $1').trim()} — {option.days} days
                  </Typography>
                  <Typography className={classes.detailText}>
                    Factor: {option.factor}
                  </Typography>
                  <Typography className={classes.detailText}>
                    Remaining Balance: {convertToPriceFormat(option.remaining)}
                  </Typography>
                  <Typography className={classes.detailText}>
                    Deadline: {option.endDate ? format(new Date(option.endDate), 'MM/dd/yyyy') : 'N/A'}
                  </Typography>
                </div>
              }
              disabled={!option.isActive || option.remaining <= 0}
            />
          </div>
        ))}

        <div className={classes.optionWrapper}>
          <FormControlLabel
            value="__none__"
            control={<Radio color="secondary" className={classes.smallRadio} />}
            label={
              <div className={classes.labelBox}>
                <Typography className={classes.optionTitle} color="textSecondary">
                  No Discount
                </Typography>
                <Typography className={classes.detailText}>
                  Select this if you don`t want to apply any early discount.
                </Typography>
              </div>
            }
          />
        </div>
      </RadioGroup>


      <div className={classes.actions}>
        <Button onClick={cancel} disabled={isLoading}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={selectedType === undefined || isLoading}
        >
          {isLoading ? <CircularProgress size={20} /> : 'Confirm'}
        </Button>
      </div>
    </div>
  );
};
